/*
 * CW Bestseller Products: client-side filters for the bestseller collection page.
 *
 * Every product card carries its filter data as attributes (rendered by sections/cw-bsc-products.liquid):
 *   data-available   "true" | "false"
 *   data-product-id  the product id, looked up in config.kitSources for the Kit Type group
 *   data-difficulty  "complete_beginner" | "beginner" | "intermediate" | ""
 *   data-hours       "Medium | 7-10 | Long Project;Large | 10-15"  (one entry per variant)
 *
 * Groups are combined with AND, options inside a group with OR. Option counts show how many products
 * would match if that option were added to the current selection.
 *
 * Kit Type is collection based: config.kitSources lists the product ids of the page's own collection ("page") and
 * of each Kit Type collection ("all", "bag", "blanket"). With no Kit Type ticked only "page" products show.
 */
(function () {
  'use strict';

  var GROUPS = ['availability', 'kit', 'difficulty', 'time', 'hours'];
  var URL_PREFIX = 'bsc_';

  function unique(list) {
    return list.filter(function (item, index) {
      return item !== '' && item !== null && list.indexOf(item) === index;
    });
  }

  function bucketFromName(name) {
    var value = (name || '').toLowerCase();
    if (value.indexOf('quick') !== -1) return 'quick';
    if (value.indexOf('slow') !== -1 || value.indexOf('steady') !== -1) return 'slow';
    if (value.indexOf('long') !== -1) return 'long';
    return '';
  }

  function parseHours(raw, config) {
    if (!raw) return [];

    return raw.split(';').map(function (line) {
      // Read the hours from the first segment that starts with a number, so labels like "Medium (70cm)" are skipped.
      var hoursPart = line.split('|').filter(function (part) { return /^\s*\d/.test(part); })[0] || line;
      var match = hoursPart.match(/(\d+(?:\.\d+)?)\s*(?:[-–—]|to)\s*(\d+(?:\.\d+)?)/i) || hoursPart.match(/(\d+(?:\.\d+)?)/);
      if (!match) return null;

      var min = parseFloat(match[1]);
      var max = match[2] ? parseFloat(match[2]) : min;

      var bucket = '';
      line.split('|').forEach(function (part) {
        bucket = bucketFromName(part) || bucket;
      });

      if (!bucket) {
        if (max <= config.quickMax) bucket = 'quick';
        else if (max <= config.longMax) bucket = 'long';
        else bucket = 'slow';
      }

      return { min: min, max: max, bucket: bucket };
    }).filter(Boolean);
  }

  // A variant lands in the highest threshold at or below its minimum hours ("7-10h" -> "5hr+").
  function thresholdFor(hours, thresholds) {
    var result = null;
    thresholds.forEach(function (threshold) {
      if (hours >= threshold) result = threshold;
    });
    return result === null ? '' : String(result);
  }

  function CwBscProducts(root) {
    this.root = root;

    var configNode = root.querySelector('[data-cw-bsc-config]');
    var config = configNode ? JSON.parse(configNode.textContent) : {};

    this.config = {
      live: config.live !== false,
      syncUrl: config.syncUrl !== false,
      quickMax: Number(config.quickMax) || 6,
      longMax: Number(config.longMax) || 15,
      countSingular: config.countSingular || '[count] Product',
      countPlural: config.countPlural || '[count] Products',
      thresholds: String(config.thresholds || '')
        .split(',')
        .map(function (value) { return parseFloat(value); })
        .filter(function (value) { return !isNaN(value); })
        .sort(function (a, b) { return a - b; }),
      kitSources: config.kitSources || {}
    };

    this.inputs = Array.prototype.slice.call(root.querySelectorAll('[data-cw-bsc-input]'));
    this.countNode = root.querySelector('[data-cw-bsc-count]');
    this.emptyNode = root.querySelector('[data-cw-bsc-empty]');
    this.grid = root.querySelector('[data-cw-bsc-grid]');
    this.products = this.readProducts();
    this.applied = this.emptyState();

    this.bindEvents();
    this.readUrl();
    this.apply({ updateUrl: false });
  }

  CwBscProducts.prototype.readProducts = function () {
    var self = this;

    var sources = this.config.kitSources;

    return Array.prototype.slice.call(this.root.querySelectorAll('[data-cw-bsc-card]')).map(function (card, index) {
      var variants = parseHours(card.getAttribute('data-hours'), self.config);
      var id = Number(card.getAttribute('data-product-id'));
      var kits = Object.keys(sources).filter(function (source) {
        return (sources[source] || []).indexOf(id) !== -1;
      });

      return {
        el: card,
        id: id,
        index: index,
        values: {
          availability: [card.getAttribute('data-available') === 'true' ? 'in_stock' : 'out_of_stock'],
          kit: kits,
          difficulty: unique([card.getAttribute('data-difficulty') || '']),
          time: unique(variants.map(function (variant) { return variant.bucket; })),
          hours: unique(variants.map(function (variant) { return thresholdFor(variant.min, self.config.thresholds); }))
        }
      };
    });
  };

  CwBscProducts.prototype.emptyState = function () {
    var state = {};
    GROUPS.forEach(function (group) { state[group] = []; });
    return state;
  };

  /* ---------- State <-> checkboxes ---------- */

  // An empty kit selection means "the page's own collection" (best sellers).
  CwBscProducts.prototype.readInputs = function () {
    var state = this.emptyState();

    this.inputs.forEach(function (input) {
      var group = input.getAttribute('data-group');
      if (!input.checked || !state[group]) return;
      state[group].push(input.value);
    });

    return state;
  };

  CwBscProducts.prototype.writeInputs = function (state) {
    this.inputs.forEach(function (input) {
      var group = input.getAttribute('data-group');
      input.checked = (state[group] || []).indexOf(input.value) !== -1;
    });
  };

  CwBscProducts.prototype.onInputChange = function (input) {
    if (input.getAttribute('data-group') === 'kit') {
      var kitInputs = this.inputs.filter(function (item) { return item.getAttribute('data-group') === 'kit'; });

      // "All Kits" already contains the specific kit types, so it and they are mutually exclusive.
      if (input.checked) {
        kitInputs.forEach(function (item) {
          if (item === input) return;
          if (input.value === 'all' || item.value === 'all') item.checked = false;
        });
      }
    }

    if (this.config.live) {
      this.apply();
    } else {
      this.updateCounts(this.readInputs());
    }
  };

  /* ---------- Matching ---------- */

  CwBscProducts.prototype.matches = function (product, state, skipGroup) {
    return GROUPS.every(function (group) {
      if (group === skipGroup) return true;
      var selected = state[group];
      if (group === 'kit' && (!selected || selected.length === 0)) selected = ['page'];
      if (!selected || selected.length === 0) return true;
      return selected.some(function (value) { return product.values[group].indexOf(value) !== -1; });
    });
  };

  CwBscProducts.prototype.updateCounts = function (state) {
    var self = this;

    this.inputs.forEach(function (input) {
      var group = input.getAttribute('data-group');
      var value = input.value;
      var count = 0;

      self.products.forEach(function (product) {
        if (!self.matches(product, state, group)) return;
        if (product.values[group].indexOf(value) !== -1) count += 1;
      });

      var option = input.closest('.cw-bsc-option');
      var countNode = option && option.querySelector('[data-cw-bsc-option-count]');
      if (countNode) countNode.textContent = '(' + count + ')';
      if (option) {
        option.classList.toggle('is-empty', count === 0);
        option.classList.toggle('is-checked', input.checked);
      }
    });
  };

  CwBscProducts.prototype.apply = function (options) {
    options = options || {};
    var self = this;
    var state = this.readInputs();
    var visible = 0;

    this.applied = state;

    this.products.forEach(function (product) {
      var show = self.matches(product, state);
      product.el.hidden = !show;
      if (show) visible += 1;
    });

    if (this.countNode) {
      var template = visible === 1 ? this.config.countSingular : this.config.countPlural;
      this.countNode.textContent = template.replace('[count]', visible);
    }

    if (this.emptyNode) this.emptyNode.hidden = visible > 0;

    this.sortCards(state);

    this.updateCounts(state);

    if (options.updateUrl !== false) this.writeUrl();

    document.dispatchEvent(new CustomEvent('cw-bsc:filters-changed', {
      detail: { kit: this.getKitType(), state: state, visible: visible }
    }));
  };

  // Show cards in the order of the collection(s) being viewed.
  CwBscProducts.prototype.sortCards = function (state) {
    if (!this.grid) return;

    var sources = this.config.kitSources;
    var order = [];
    (state.kit.length ? state.kit : ['page']).forEach(function (source) {
      order = order.concat(sources[source] || []);
    });

    var rank = function (product) {
      var position = order.indexOf(product.id);
      return position === -1 ? order.length + product.index : position;
    };

    var grid = this.grid;
    this.products
      .slice()
      .sort(function (a, b) { return rank(a) - rank(b); })
      .forEach(function (product) { grid.appendChild(product.el); });
  };

  CwBscProducts.prototype.reset = function () {
    this.writeInputs(this.emptyState());
    this.apply();
  };

  /* ---------- URL ---------- */

  CwBscProducts.prototype.readUrl = function () {
    if (!this.config.syncUrl) return;

    var params = new URLSearchParams(window.location.search);
    var state = this.emptyState();
    var hasAny = false;

    GROUPS.forEach(function (group) {
      var raw = params.get(URL_PREFIX + group);
      if (!raw) return;
      state[group] = raw.split(',').filter(Boolean);
      hasAny = hasAny || state[group].length > 0;
    });

    if (hasAny) this.writeInputs(state);
  };

  CwBscProducts.prototype.writeUrl = function () {
    if (!this.config.syncUrl || !window.history.replaceState) return;

    var url = new URL(window.location.href);
    var state = this.applied;

    GROUPS.forEach(function (group) {
      if (state[group].length) {
        url.searchParams.set(URL_PREFIX + group, state[group].join(','));
      } else {
        url.searchParams.delete(URL_PREFIX + group);
      }
    });

    window.history.replaceState(window.history.state, '', url.toString());
  };

  /* ---------- Public helpers (used by the categories section) ---------- */

  CwBscProducts.prototype.getKitType = function () {
    var kits = this.applied.kit;
    if (kits.length === 0) return '';
    return kits.length === 1 ? kits[0] : 'multiple';
  };

  CwBscProducts.prototype.setKitType = function (kit, options) {
    var state = this.readInputs();
    state.kit = kit ? [kit] : [];
    this.writeInputs(state);
    this.apply();

    if (options && options.scroll) this.scrollToTop();
  };

  CwBscProducts.prototype.scrollToTop = function () {
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ---------- Events ---------- */

  CwBscProducts.prototype.isStacked = function () {
    return window.matchMedia('(max-width: 989px)').matches;
  };

  CwBscProducts.prototype.setPanelOpen = function (open) {
    var toggle = this.root.querySelector('[data-cw-bsc-filters-toggle]');
    this.root.classList.toggle('is-filters-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  CwBscProducts.prototype.bindEvents = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('change', function (event) {
      if (event.target.matches('[data-cw-bsc-input]')) self.onInputChange(event.target);
      if (event.target.matches('[data-cw-bsc-sort]')) self.onSortChange(event.target.value);
    });

    root.addEventListener('click', function (event) {
      var groupToggle = event.target.closest('[data-cw-bsc-group-toggle]');
      if (groupToggle) {
        var expanded = groupToggle.getAttribute('aria-expanded') === 'true';
        groupToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        return;
      }

      var panelToggle = event.target.closest('[data-cw-bsc-filters-toggle]');
      if (panelToggle) {
        // The panel only collapses in the stacked (mobile / tablet) layout.
        if (self.isStacked()) self.setPanelOpen(!root.classList.contains('is-filters-open'));
        return;
      }

      if (event.target.closest('[data-cw-bsc-apply]')) {
        self.apply();
        if (self.isStacked()) {
          self.setPanelOpen(false);
          self.scrollToTop();
        } else if (root.getBoundingClientRect().top < 0) {
          self.scrollToTop();
        }
        return;
      }

      if (event.target.closest('[data-cw-bsc-reset]')) {
        self.reset();
      }
    });
  };

  CwBscProducts.prototype.onSortChange = function (value) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    url.hash = this.root.id || '';
    window.location.href = url.toString();
  };

  /* ---------- Boot ---------- */

  function init(scope) {
    var root = (scope || document).querySelector('[data-cw-bsc-products]');
    if (!root) return;

    var instance = new CwBscProducts(root);

    window.CwBscFilters = {
      setKitType: function (kit, options) { instance.setKitType(kit, options); },
      getKitType: function () { return instance.getKitType(); },
      reset: function () { instance.reset(); }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-cw-bsc-products]')) init(event.target);
  });
})();
